import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../../components/Spin";
import { Label } from "../../../../components/ui/label";
import CustomLabelText from "../../../../components/CustomLabelText";
import { API, fileUpload, get } from "../../../../config";
import SearchSelect from "../../../../components/SearchSelect";
import { Button } from "../../../../components/ui/button";
import { AddAdminPolicySchema } from "../../../../lib/Validation";
import { useAllOrganizationTypes } from "../../../Auth/SignUpHelper";
import { Loader2 } from "lucide-react";
import MultiFileUpload from "../../../../components/MultiFileUpload";

const AddEditPolicy = ({
  id,
  toggleShowAddEditPolicy,
  edit,
  fetchPolicyData,
}) => {
  const [policyData, setPolicyData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [versionLoading, setVersionLoading] = useState(false);
  const [versionListing, setVersionListing] = useState([]);
  const [versionList, setVersionList] = useState([]);

  const [conditionLoading, setConditionLoading] = useState(false);
  const [conditionListing, setConditionListing] = useState([]);
  const [conditionList, setConditionList] = useState([]);

  const { allOrganizationTypeLoading, allOrganizationType = [] } =
    useAllOrganizationTypes({ amlCTFType: true });
  const organizationType = allOrganizationType?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name, value, ...rest };
  });

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.ADMIN_POLICY_DETAILS}/` + id);
      setPolicyData(reponse?.data?.data);

      AdminConditionList(reponse?.data?.data?.organization_type_id);
      setLoading(false);
    } catch (e) {
      setPolicyData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();
    formData.append(
      "organization_type_id",
      value?.organization_type_id ? value?.organization_type_id : ""
    );
    formData.append("version_id", value?.version_id ? value?.version_id : "");

    formData.append(
      "condition_id",
      value?.condition_id ? value?.condition_id : ""
    );

    formData.append(
      "policy_file",
      value?.policy_file
        ? value?.policy_file?.[0]?.url
          ? value?.policy_file?.[0]?.url
          : value?.policy_file?.[0]?.file
        : ""
    );

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.ADMIN_POLICY_ADD}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditPolicy();
        fetchPolicyData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  const AdminVersionList = async () => {
    try {
      setVersionLoading(true);

      const { data } = await get(`${API.GET_ADMIN_VERSIONS}?limit=-1&page=1`);

      setVersionLoading(false);

      setVersionList(() => [...data.data.data]);
    } catch (e) {
      setVersionList([]);
      setVersionLoading(false);
      return false;
    }
  };

  useEffect(() => {
    AdminVersionList();
  }, []);

  useEffect(() => {
    if (versionList) {
      let versionTempListing = versionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: `${item?.version_number} - ${item?.name}`,
      }));
      setVersionListing(versionTempListing || []);
    } else {
      setVersionListing([]);
    }
  }, [versionList]);

  const AdminConditionList = async (organizationType) => {
    try {
      setConditionLoading(true);

      const { data } = await get(
        `${API.GET_ADMIN_CONDITIONS}?limit=-1&page=1&organization_type_id=${organizationType}`
      );

      setConditionLoading(false);

      setConditionList(() => [...data.data.data]);
    } catch (e) {
      setConditionList([]);
      setConditionLoading(false);
      return false;
    }
  };

  useEffect(() => {
    if (conditionList) {
      let conditionTempListing = conditionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: `${item?.risk_level} - ${item?.section_type}`,
      }));
      setConditionListing(conditionTempListing || []);
    } else {
      setConditionListing([]);
    }
  }, [conditionList]);

  return (
    <Formik
      initialValues={{
        organization_type_id: policyData?.organization_type_id,
        version_id: policyData?.version_id,
        policy_file: policyData?.policy_file
          ? [
              {
                name: policyData?.policy_file?.split("/")?.slice(-1)[0],
                url: policyData?.policy_file,
              },
            ]
          : [],
        condition_id: policyData?.condition_id ? policyData?.condition_id : "",
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddAdminPolicySchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-policy md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-1 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Industry Type"
                        errorField={errors?.organization_type_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={organizationType}
                        optionType={"Industry Type"}
                        handleSelect={(e) => {
                          setFieldValue("organization_type_id", e);
                          AdminConditionList(e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.organization_type_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.organization_type_id}
                        disabled={false}
                        onClear={() => {
                          setFieldValue("organization_type_id", "");
                          setConditionList([]);
                        }}
                      />
                      {allOrganizationTypeLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={false}
                        labelText="Conditions"
                        errorField={errors?.condition_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={conditionListing}
                        optionType={"Conditions"}
                        handleSelect={(e) => {
                          setFieldValue("condition_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.condition_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        clearable
                        onClear={() => {
                          setFieldValue("condition_id", "");
                        }}
                        initialValue={values?.condition_id}
                        disabled={!values?.organization_type_id}
                      />
                      {conditionLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Version Number"
                        errorField={errors?.version_id}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={versionListing}
                        optionType={"Version Number"}
                        handleSelect={(e) => {
                          setFieldValue("version_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.version_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.version_id}
                        disabled={false}
                      />
                      {versionLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="files">
                      <CustomLabelText
                        required={true}
                        labelText="Policy File"
                        errorField={errors?.policy_file}
                      />
                    </Label>
                    <MultiFileUpload
                      files={values?.policy_file}
                      setFiles={(data) => {
                        setFieldValue("policy_file", data);
                      }}
                      multiple={false}
                      accept=".pdf"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditPolicy();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditPolicy;
